import { useId, forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { useLanguage } from '@hooks/useLanguage';

export type InputState = 'default' | 'informative' | 'error' | 'success' | 'disabled';
export type InputSize = 'sm' | 'md' | 'lg';
export type InputType = 'text' | 'currency' | 'tel';

export interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  state?: InputState;
  size?: InputSize;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  showHelperIcon?: boolean;
  /** Root (label) follows this; omit to use document dir. */
  dir?: 'ltr' | 'rtl';
  /** When set, only the inner field (wrapper + input) use this dir; root/label follow document dir. Use for e.g. phone: label follows Arabic layout while digits stay LTR via contentDir="ltr" + `.force-ltr`. */
  contentDir?: 'ltr' | 'rtl';
  fullWidth?: boolean;
}

const sizeClasses = {
  sm: {
    root: 'gap-2',
    label: 'text-sm',
    wrapper: 'min-h-[42px] py-2.5 px-3 gap-2',
    input: 'text-sm',
    helper: 'text-xs',
  },
  md: {
    root: 'gap-2',
    label: 'text-sm',
    wrapper: 'min-h-[46px] py-3 px-3 gap-2',
    input: 'text-sm',
    helper: 'text-sm',
  },
  lg: {
    root: 'gap-2',
    label: 'text-base',
    wrapper: 'min-h-[50px] py-3 px-4 gap-2',
    input: 'text-base',
    helper: 'text-sm',
  },
} as const;

/* Sa3ee design system: Primary focus, Secondary success, surface-error-primary for error */
const wrapperStateClasses: Record<InputState, string> = {
  default:
    'bg-surface-cards border-surface-gray-darker focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20',
  informative:
    'bg-surface-cards border-surface-gray-darker focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20',
  error:
    'border-surface-error-primary focus-within:border-surface-error-primary focus-within:ring-2 focus-within:ring-surface-error-primary/20',
  success:
    'border-secondary focus-within:border-secondary focus-within:ring-2 focus-within:ring-secondary/20',
  disabled: 'bg-surface-gray-lighter border-surface-gray-darker cursor-not-allowed focus-within:ring-0',
};

const helperTextStateClasses: Record<InputState, string> = {
  default: 'text-gray-700',
  informative: 'text-blue-700',
  error: 'text-surface-error-primary',
  success: 'text-secondary',
  disabled: 'text-gray-500',
};

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(function InputField(
  {
    label,
    helperText,
    state = 'default',
    size = 'lg',
    leftAddon,
    rightAddon,
    showHelperIcon = true,
    dir,
    contentDir: contentDirProp,
    fullWidth,
    className = '',
    id: idProp,
    disabled,
    ...inputProps
  },
  ref
) {
  const generatedId = useId();
  const { isRtl: appIsRtl } = useLanguage();
  const documentDir = (appIsRtl ? 'rtl' : 'ltr') as 'ltr' | 'rtl';
  const effectiveDir = dir ?? documentDir;
  const contentDir = contentDirProp ?? effectiveDir;
  const isContentRtl = contentDir === 'rtl';
  const isDisabled = disabled || state === 'disabled';
  const id = idProp ?? `ds-input-${generatedId.replace(/:/g, '')}`;
  const helperId = `${id}-helper`;
  const sizeCls = sizeClasses[size];

  const rootClasses = [
    'flex flex-col w-full font-sans',
    fullWidth ? 'max-w-none' : 'max-w-xs',
    sizeCls.root,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [
    'flex items-center border border-solid rounded-[16px] transition-[border-color,box-shadow]',
    sizeCls.wrapper,
    contentDirProp != null ? 'flex-row' : rightAddon ? 'flex-row' : isContentRtl ? 'flex-row-reverse' : 'flex-row',
    wrapperStateClasses[state],
    'has-[:disabled]:bg-surface-gray-lighter has-[:disabled]:cursor-not-allowed has-[:disabled]:focus-within:ring-0',
  ].join(' ');

  const inputClasses = [
    'flex-1 min-w-0 border-0 bg-transparent font-sans outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-80 text-gray-900',
    // Explicit LTR/RTL edges (Arabic mode: right-aligned text + placeholders for reading flow).
    isContentRtl ? 'text-right' : 'text-left',
    // WebKit/Blink often ignore start alignment for ::placeholder; force edge to match content direction.
    isContentRtl ? '[&::placeholder]:text-right' : '[&::placeholder]:text-left',
    sizeCls.input,
  ].join(' ');

  const rootDir = contentDirProp != null ? documentDir : effectiveDir;
  const helperClasses = [
    'flex items-center gap-1 min-h-0 font-sans text-start',
    sizeCls.helper,
    'leading-normal',
    helperTextStateClasses[state],
    'flex-row justify-start',
  ].join(' ');

  return (
    <div className={rootClasses} dir={rootDir} data-state={state} data-size={size}>
      {label && (
        <label
          htmlFor={id}
          dir={rootDir}
          className={`m-0 block w-full font-normal text-gray-900 ${sizeCls.label} text-start`}
        >
          {label}
        </label>
      )}
      <div className={wrapperClasses} dir={contentDir}>
        {leftAddon && (
          <span className="inline-flex items-center justify-center shrink-0 text-gray-700">{leftAddon}</span>
        )}
        <input
          ref={ref}
          id={id}
          type={inputProps.type ?? 'text'}
          dir={contentDir}
          className={inputClasses}
          disabled={isDisabled}
          aria-invalid={state === 'error' ? true : undefined}
          aria-describedby={helperText ? helperId : undefined}
          aria-disabled={isDisabled ? true : undefined}
          {...inputProps}
        />
        {rightAddon && (
          <span className="inline-flex items-center justify-center shrink-0 text-gray-700">{rightAddon}</span>
        )}
      </div>
      {helperText && (
        <div id={helperId} dir={rootDir} className={helperClasses} role={state === 'error' ? 'alert' : undefined}>
          {showHelperIcon && state !== 'default' && state !== 'disabled' && (
            <span
              className={`inline-flex shrink-0 ${
                state === 'error'
                  ? 'text-surface-error-primary'
                  : state === 'success'
                    ? 'text-secondary'
                    : 'text-blue-600'
              }`}
              aria-hidden
            >
              {state === 'error' && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 4v4M8 10v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              )}
              {state === 'success' && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M5 8l2.5 2.5L11 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {state === 'informative' && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 7v4M8 5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              )}
            </span>
          )}
          <span>{helperText}</span>
        </div>
      )}
    </div>
  );
});
